import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CheckPrinter from '../../../assets/lotties/check_Printer.json';
import FundsErrorIcon from '../../../assets/icons/svg/stamp-icons/ic-stamp-happy-dollar.svg';
import { ReactComponent as EditIcon } from '../../../assets/svg/edit-pen.svg';
import { ReactComponent as RefreshIcon } from '../../../assets/svg/refresh.svg';
import CloseIcon, { ReactComponent as XIcon } from '../../../assets/svg/x-icon-v2.svg';
import Button from '../../../generic-components/Button';
import Heading from '../../../generic-components/Heading';
import Icon from '../../../generic-components/Icon';
import Lottie from '../../../generic-components/Lottie';
import { UserMessage } from '../../../DesignSystem/LiliUserMessage/UserMessage';
import { convertValueToUSD } from '../../../helpers/currency.helper';
import { VIEWS_MAP } from '../../../router/constants';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { takeSnapshot } from '../../../services/snapshot.service';
import { useStore } from '../../../store';
import { IServerErrorResponse } from '../../../types';
import { SUPPLIER_REFERRER_TYPES } from '../../Supplier/consts';
import { getAddressDetailsHtml } from '../../Supplier/helpers';
import { MANUAL_HANDLE_ERROR_CODES, REVIEW_MEMO_MAX_LEN, STEPS, STYLES } from '../consts';
import { postMailCheckReview } from '../../MoveMoney/api/postMailCheckReview';
import { usePostCheckConfirmation } from '../hooks/usePostCheckConfirmation';
import { HeaderNotification } from './HeaderNotification';

export const Review = () => {
  const {
    setActiveStep,
    checkAmount,
    payeeDetails,
    feeData,
    getPrimaryAccount,
    user,
    setUpperMessageContent,
    upperMessageContent,
    setIsMailCheckMemoAdded,
    billId,
  } = useStore((state) => state);
  const { balance } = getPrimaryAccount();
  const { t } = useTranslation();
  const [memoValue, setMemoValue] = useState<string>('');
  const [showEditPayeeOptions, setShowEditPayeeOptions] = useState<boolean>(false);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState<boolean>(false);
  const [showFundsError, setShowFundsError] = useState<boolean>(false);
  const [isSendCheckInitiated, setIsSendCheckInitiated] = useState<boolean>(false);
  const navigate = useNavigate();
  const { mutateAsync: sendCheck, isLoading: isSendingCheck } = postMailCheckReview();
  const { mutateAsync: uploadSendCheckConfirmation } = usePostCheckConfirmation();

  const navigateState = {
    closeUrl: `/${VIEWS_MAP.MAIL_A_CHECK}`,
    referrer: SUPPLIER_REFERRER_TYPES.MAIL_CHECK,
    desireStep: STEPS.REVIEW,
    backUrl: `/${VIEWS_MAP.MAIL_A_CHECK}`,
  };

  const handleEditPayee = () => {
    setShowEditPayeeOptions((prevState) => !prevState);
  };

  const editPayeeDetails = () => {
    trackMixPanel('Act on Move Money Review Screen', { Action: 'Edit Payee' });
    navigate(`/${VIEWS_MAP.SUPPLIER.BASE}/${VIEWS_MAP.SUPPLIER.MANAGE.BASE}/${payeeDetails?.supplierId}`, {
      state: {
        pageTitle: 'Mail a paper check',
        formHeader: 'Enter recipient\'s details',
        formType: 'MAIL_A_CHECK',
        onExitDestination: `/${VIEWS_MAP.DASHBOARD}`,
        isUpdateOnlyMode: true,
      },
    });
  };

  const switchPayee = () => {
    trackMixPanel('Act on Move Money Review Screen', { Action: 'Switch Payee' });
    navigate(`/${VIEWS_MAP.SUPPLIER.BASE}/${VIEWS_MAP.SUPPLIER.PICKER}`, {
      state: navigateState,
    });
  };

  const handlePostCreation = async (transactionId: string) => {
    if (transactionId) {
      const fdPayload: any = await takeSnapshot();
      await uploadSendCheckConfirmation({ customerId: user.customerId, transactionId, fdPayload });
      setActiveStep(STEPS.SUCCESS);
    }
  };

  const handleSubmit = async () => {
    trackMixPanel('Act on Move Money Review Screen', { Action: 'Confirm and Mail the Check' });
    if (memoValue) setIsMailCheckMemoAdded(true);
    sendCheck({
      amount: checkAmount,
      memo: memoValue,
      supplierId: payeeDetails?.supplierId || '',
      billId,
    })
      .then((sendCheckRes) => {
        if (sendCheckRes?.transactionId) {
          handlePostCreation(sendCheckRes.transactionId || '');
        }
      })
      .catch((e: IServerErrorResponse) => {
        const { localizedMessage, code } = e.response.data.errors[0];
        if (code === MANUAL_HANDLE_ERROR_CODES.INSUFFICIENT_FUNDS) {
          setShowFundsError(true);
          setIsSendCheckInitiated(false);
        } else if (localizedMessage) {
          toast.error(localizedMessage, {
            position: 'bottom-center',
            autoClose: 2500,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: false,
            progress: undefined,
            theme: 'colored',
          });
        }
      });
  };

  const getFundsErrorMessage = () => {
    if (balance - feeData.fee < 0) {
      return t('MAIL_CHECK.REVIEW.FUNDS_ERROR_MESSAGE.NEGATIVE_BALANCE_MESSAGE').replace(
        '{FEE_AMOUNT}',
        convertValueToUSD(feeData.fee, 2),
      );
    }
    return t('MAIL_CHECK.REVIEW.FUNDS_ERROR_MESSAGE.MESSAGE')
      .replace('{FEE_AMOUNT}', convertValueToUSD(feeData.fee, 2))
      .replace('{CHECK_AMOUNT}', convertValueToUSD(balance - feeData.fee, 2));
  };

  const handleEditAmount = () => {
    trackMixPanel('Act on Move Money Review Screen', { Action: 'Edit Amount' });
    setActiveStep(STEPS.AMOUNT);
  };

  useEffect(() => {
    if (isSendingCheck) {
      setIsSendCheckInitiated(true);
    }
  }, [isSendingCheck]);

  useEffect(() => {
    trackMixPanel('View Move Money Review Screen', { Flow: 'Mail a Check' });
  }, []);

  const isVendorDataValid = useMemo(() => {
    if (!payeeDetails?.supplierAddressData) return false;
    const { line1, postalCode, state, city } = payeeDetails.supplierAddressData;
    const requierdFields = [line1, postalCode, city, state];

    return requierdFields.every((i) => i);
  }, [payeeDetails]);

  return (
    <>
      {showFundsError && (
        <UserMessage onClick={() => setShowFundsError(false)} id='fundsErrorModal'>
          <div className='py-[24px] bg-bw00 px-[24px] rounded-[8px] border border-bw03 relative flex flex-col items-center justify-center fadeIn'>
            <Button
              onClick={() => setShowFundsError(false)}
              childElement={<XIcon className='stroke-bw08 w-[20px] hover:stroke-red02' />}
              className='absolute right-[12px] top-[12px]'
              id='closeFundsErrorMessage'
            />
            <Icon className='w-[80px] mb-[24px]' src={FundsErrorIcon} />
            <Heading
              title={t('MAIL_CHECK.REVIEW.FUNDS_ERROR_MESSAGE.TITLE')}
              className='font-metricR text-[22px] text-bw08 mb-[4px]'
            />
            <Heading
              title={getFundsErrorMessage()}
              className='font-metricR text-[18px] text-bw07 text-center max-w-[310px] mb-[22px]'
            />
            <Button
              onClick={() => setActiveStep(STEPS.AMOUNT)}
              className='w-[300px] rounded-[4px] bg-action03 hover:bg-action04 py-[17px] font-metricR text-bw00 text-[16px]'
              id='modifyAmount'
              title={t('MAIL_CHECK.REVIEW.FUNDS_ERROR_MESSAGE.CTA')}
            />
          </div>
        </UserMessage>
      )}
      {showCloseConfirmation && (
        <UserMessage onClick={() => setShowCloseConfirmation(false)} id='confirmCloseMessage'>
          <div className='w-[530px] py-[24px] bg-bw00 px-[24px] rounded-[8px] border border-bw03 relative flex flex-col'>
            <Button
              onClick={() => setShowCloseConfirmation(false)}
              childElement={<XIcon className='stroke-bw08 w-[20px] hover:stroke-red02' />}
              className='absolute right-[12px] top-[12px]'
              id='closeConfirmation'
            />
            <Heading
              title={t('MAIL_CHECK.REVIEW.CLOSE_CONFIRMATION.TITLE')}
              className='font-metricR text-[24px] text-bw08 leading-[128%] mb-[4px]'
            />
            <Heading
              title={t('MAIL_CHECK.REVIEW.CLOSE_CONFIRMATION.DISCLAIMER')}
              className='font-metricR text-[16px] text-bw07 leading-[128%] mb-[26px]'
            />
            <div className='w-full flex items-center justify-end gap-[12px]'>
              <Button
                onClick={() => setShowCloseConfirmation(false)}
                className='bg-bw02 hover:bg-bw03 py-[11px] px-[40px] font-metricR text-[14px] text-action03 rounded-[4px]'
                title={t('MAIL_CHECK.REVIEW.CLOSE_CONFIRMATION.CTA_STAY')}
                id='stayOnPage'
              />
              <Link
                className='bg-red02 hover:bg-red01 py-[11px] px-[40px] font-metricR text-[14px] text-bw00 rounded-[4px]'
                id='leavePage'
                to={VIEWS_MAP.DASHBOARD}
              >
                {t('MAIL_CHECK.REVIEW.CLOSE_CONFIRMATION.CTA_LEAVE')}
              </Link>
            </div>
          </div>
        </UserMessage>
      )}
      {isSendCheckInitiated && (
        <UserMessage onClick={() => {}} id='loaderScreen'>
          <Lottie lottieAsset={CheckPrinter} isLoop isAutoplay className='w-full' />
        </UserMessage>
      )}
      <div className={STYLES.AMOUNT.HEADER_WRAPPER}>
        <Button
          className='rounded-[50%] hover:bg-bw02 py-[4px] px-[4px] flex items-center justify-center'
          id=''
          childElement={
            <Icon
              onClick={() => setShowCloseConfirmation(true)}
              className={STYLES.AMOUNT.HEADER_ICON}
              src={CloseIcon}
            />
          }
        />
        <Heading title={t('MAIL_CHECK.AMOUNT.HEADER_TITLE')} className={STYLES.AMOUNT.HEADER_TITLE} />
        <span />
        {upperMessageContent && (
          <HeaderNotification
            autoCloseSec={8}
            onTimeElapsed={() => setUpperMessageContent('')}
            title={upperMessageContent}
          />
        )}
      </div>
      <div className={STYLES.AMOUNT.BODY_WRAPPER}>
        <div className='absolute z-50 inset-0 pt-[64px] flex flex-col items-center overflow-y-scroll pb-[14px]'>
          <Heading title={t('MAIL_CHECK.REVIEW.BODY.TITLE')} className='font-metricR text-[28px] text-bw08 mb-[16px]' />
          <div className='w-[432px] py-[16px] px-[16px] flex flex-col rounded-[12px] border border-bw03 mb-[12px]'>
            <div className='w-full flex items-center justify-between mb-[12px]'>
              <Heading
                id='transferDetailsHeader'
                title={t('MAIL_CHECK.REVIEW.BODY.ACTION_1_TITLE')}
                className='font-metricR text-[18px] text-bw08 leading-[150%]'
              />
              <Button
                onClick={handleEditAmount}
                className='font-metricR text-[16px] text-bw08 hover:bg-bw03 py-[4px] hover:rounded-[4px]'
                title={t('MAIL_CHECK.REVIEW.BODY.CTA_EDIT')}
                id='editAmount'
              />
            </div>
            <div className='w-full flex items-center justify-between pb-[16px] border-b border-bw03 mb-[16px]'>
              <Heading
                id='amountHeader'
                title={t('MAIL_CHECK.REVIEW.BODY.AMOUNT')}
                className='font-metricR text-[16px] text-bw07 leading-[150%]'
              />
              <Heading
                id='amountValue'
                title={convertValueToUSD(checkAmount, 2)}
                className='font-metricRSemibold text-[22px] text-action03 leading-[128%]'
              />
            </div>

            <div className='w-full flex items-center justify-between mb-[12px]'>
              <Heading
                id='payeeDetailsHeader'
                title={t('MAIL_CHECK.REVIEW.BODY.PAYEE_DETAILS')}
                className='font-metricR text-[18px] text-bw08 leading-[150%]'
              />
              <Button
                onClick={handleEditPayee}
                className='font-metricR text-[16px] text-bw08 hover:bg-bw03 py-[4px]  hover:rounded-[4px] relative'
                title={t('MAIL_CHECK.REVIEW.BODY.CTA_EDIT')}
                id='editDetails'
                childElement={
                  showEditPayeeOptions ? (
                    <div className='w-[208px] bg-bw00 shadow-light rounded-[12px] absolute left-0 top-[50%] translate-x-[-103%] translate-y-[-50%] flex flex-col fadeIn'>
                      <Button
                        onClick={editPayeeDetails}
                        className='w-full hover:bg-bw02'
                        id='editPayee'
                        childElement={
                          <div className='flex items-center justify-center gap-[6px] py-[16px] border-b border-bw03'>
                            <EditIcon className='w-[24px] stroke-bw08' />
                            <Heading
                              title={t('MAIL_CHECK.REVIEW.BODY.EDIT_PAYEE')}
                              className='font-metricR text-[16px] text-bw08'
                            />
                          </div>
                        }
                      />
                      <Button
                        className='w-full hover:bg-bw02'
                        id='switchPayee'
                        onClick={switchPayee}
                        childElement={
                          <div className='flex items-center justify-center gap-[6px] py-[16px]'>
                            <RefreshIcon className='w-[24px] stroke-bw08' />
                            <Heading
                              title={t('MAIL_CHECK.REVIEW.BODY.SWITCH_PAYEE')}
                              className='font-metricR text-[16px] text-bw08'
                            />
                          </div>
                        }
                      />
                    </div>
                  ) : (
                    ''
                  )
                }
              />
            </div>

            <div className='w-full flex items-center justify-between mb-[12px]'>
              <Heading
                id='sendToHeader'
                title={t('MAIL_CHECK.REVIEW.BODY.SEND_TO')}
                className='font-metricR text-[16px] text-bw07 leading-[150%]'
              />
              <Heading
                id='sendToValue'
                title={payeeDetails?.name || ''}
                className='font-metricR text-[16px] text-bw08'
              />
            </div>

            <div className='w-full flex items-start justify-between'>
              <Heading
                id='addressHeader'
                title={t('MAIL_CHECK.REVIEW.BODY.ADDRESS')}
                className='font-metricR text-[16px] text-bw07 leading-[150%]'
              />
              {payeeDetails && isVendorDataValid ? (
                <Heading
                  id='addressValue'
                  type='HTML'
                  title={getAddressDetailsHtml(payeeDetails.supplierAddressData)}
                  className='font-metricR text-[16px] text-bw08 max-w-[160px] text-end leading-[128%]'
                />
              ) : (
                <Button
                  className='font-metricR text-[16px] text-functionalNegative hover:underline'
                  title={t('MAIL_CHECK.REVIEW.BODY.FILL_ADDRESS')}
                  id='fill-address'
                  onClick={editPayeeDetails}
                />
              )}
            </div>
          </div>

          <Heading
            title={t('MAIL_CHECK.REVIEW.BODY.MEMO')}
            className='font-metricR text-[16px] text-bw08 mb-[4px] w-[432px]'
          />
          <input
            id='memoInput'
            value={memoValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMemoValue(e.target.value)}
            maxLength={REVIEW_MEMO_MAX_LEN}
            placeholder={t('MAIL_CHECK.REVIEW.BODY.MEMO_PLACEHOLDER')}
            type='text'
            className='bg-bw00 w-[432px] py-[11px] px-[16px] font-metricR text-bw08 text-[16px] rounded-[8px] border border-bw03 focus:border-action03 caret-action03 outline-none mb-[4px]'
          />
          <div className='w-[432px] flex items-center justify-end mb-[12px]'>
            <Heading
              id='memoCountValue'
              title={`${memoValue.length}/50`}
              className='font-metricR text-[12px] text-bw08'
            />
          </div>

          <div className='w-[432px] py-[16px] flex flex-col rounded-[12px] border border-bw03 mb-[8px]'>
            <div className='w-full flex items-center justify-between mb-[12px] px-[12px] pb-[12px] border-b border-bw03'>
              <Heading
                id='estimateArrivalHeader'
                title={t('MAIL_CHECK.REVIEW.BODY.ESTIMATED_ARRIVAL')}
                className='font-metricR text-[16px] text-bw07 leading-[150%]'
              />
              <Heading
                id='estimateArrivalValue'
                title={feeData.arrivalDate}
                className='font-metricR text-[16px] text-bw08'
              />
            </div>
            <div className='w-full flex items-center justify-between px-[12px]'>
              <Heading
                id='feeHeader'
                title={t('MAIL_CHECK.REVIEW.BODY.FEE')}
                className='font-metricR text-[16px] text-bw07 leading-[150%]'
              />
              <Heading
                id='feeValue'
                title={convertValueToUSD(feeData.fee, 2)}
                className='font-metricR text-[16px] text-bw08'
              />
            </div>
          </div>

          <Heading
            title={t('MAIL_CHECK.REVIEW.BODY.DISCLAIMER')}
            className='font-metricR text-[14px] w-[432px] text-bw06 mb-[16px]'
          />
        </div>
      </div>
      <div className='w-full flex items-center justify-center py-[24px] border-t border-bw03 fadeIn'>
        <Button
          disable={isSendingCheck || !isVendorDataValid}
          isLoading={isSendingCheck}
          onClick={handleSubmit}
          title={t('MAIL_CHECK.REVIEW.BODY.SUBMIT')}
          className={`w-[432px] bg-action03 hover:bg-action04 text-sm text-bw00
           rounded py-[18px] font-metricMedium 
           ${isSendingCheck || !isVendorDataValid ? 'bg-action04 cursor-not-allowed' : 'bg-action03'}`}
          id='confirmMailCheck'
        />
      </div>
    </>
  );
};
